import "./Chatbot.scss";
import {
  Avatar,
  Box,
  CircularProgress,
  Divider,
  Drawer,
  SvgIcon,
  Typography,
} from "@mui/material";
import { FunctionComponent, useEffect, useRef, useState, useMemo } from "react";
import TypewriterMarkdown from "../TypewriterMarkdown/TypewriterMarkdown";

import IconButtonField from "../Button/IconButtonField";
import { ChatbotProps, IChatbot } from "./IChatbot";
import { useAppDispatch, useAppSelector } from "../../Redux/Store/Hooks";
import TextInputFieldC from "../TextInput/TextInputFieldC";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { ReactComponent as Send } from "../../Assets/Images/sendicon.svg";
import CloseIcon from "@mui/icons-material/Close";
import Markdown from "react-markdown";
import rehypeKatex from "rehype-katex";
import remarkMath from "remark-math";
import "katex/dist/katex.min.css";
import "katex/dist/katex.css";
import {
  chatbotStart,
  clearChatbotStatus,
} from "../../Redux/Slices/Chatbot/ChatbotSlicer";
import { ReactComponent as Chatbotboticon } from "../../Assets/Images/Chatboticonresult.svg";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

const drawerWidth = 450;

const Chatbot: FunctionComponent<ChatbotProps> = (props) => {
  const dispatch = useAppDispatch();
  const { data: chatbotData, isLoading: chatloading } = useAppSelector(
    (state) => state.ChatbotSlicer
  );
  const [isTyping, setIsTyping] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);


  const { open, setOpen, questiondata, imagedata, passage, instruction } =
    props;

  // chatbot body scroll ref
  const divRef = useRef<HTMLDivElement>(null);

  // form init
  const methods = useForm<IChatbot>();
  const formRef = useRef<HTMLFormElement>(null);

  // Clean ChatGPT HTML tags and normalize spacing
  const cleanMessage = (text = "") => {
    return text
      .replace(/<p><br><\/p>/g, "\n")
      .replace(/<br\s*\/?>/g, "\n")
      .replace(/<\/p>/g, "\n\n")
      .replace(/<p>/g, "")
      .replace(/<[^>]+>/g, "");
  };

  // form submit
  const onSubmit: SubmitHandler<IChatbot> = (data) => {
    if (chatloading || isTyping) return;

    // Format chat history for payload
    const history = chatbotData?.data || [];
    const chatContext = history
      .map(
        (chat: any) =>
          `${chat.user === "user" ? "user" : "bot"}: ${cleanMessage(
            chat.message
          )}`
      )
      .join(", ");

    const payloadChat = `Previous message:[ ${chatContext} ] , user asked message: ${data.chat}`;

    dispatch(
      chatbotStart({
        chat: payloadChat,
        question: `${questiondata}(Note: The image URL provided is for reference to be analyzed: ${imagedata})`,
        session_id: "1",
        passage: passage,
        instruction: instruction,
      })
    );
    methods.reset({ chat: "" });
  };

  useEffect(() => {
    // scroll to bottom
    divRef.current?.lastElementChild?.scrollIntoView({
      behavior: "smooth",
      block: "start",
      inline: "nearest",
    });

    const messages = chatbotData?.data;
    if (messages && messages.length > 0) {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage.user === "bot") {
        setIsTyping(true);
      }
    }
  }, [chatbotData]);

  useEffect(() => {
    if (open) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100); // slight delay ensures drawer is rendered
    }

    if (!chatloading) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100); // allow DOM update
    }
  }, [chatloading, open]);

  return (
    <>
      <Drawer
        sx={{
          width: drawerWidth,
          marginTop: "60px",
          "& .MuiDrawer-paper": {
            padding: 1,
            width: drawerWidth,
            display: "flex",
            flexDirection: "column",
          },
        }}
        variant="persistent"
        anchor="right"
        open={open}
      >
        <header className="chat_bot_header">
          <span
            style={{ fontSize: "13px", fontWeight: 600, marginTop: "15px" }}
          >
            Chat with Elliot
          </span>
          <IconButtonField
            sx={{ ml: "auto" }}
            onClick={() => {
              setOpen(false);
              dispatch(clearChatbotStatus());
            }}
          >
            <CloseIcon />
          </IconButtonField>
        </header>

        <Divider />

        <div
          ref={divRef}
          className="message_area"
          style={{ flexGrow: 1, overflowY: "auto" }}
        >
          {/* Initial message from Elliot */}
          {/* {(!chatbotData?.data || chatbotData.data.length === 0) && ( */}
          <Box sx={{ p: 2 }}>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 600,
                mb: 1,
                display: "flex",
                gap: "10px",
                alignItems: "center",
              }}
            >
              <Avatar
                src="../../Assets/Images/Chatbot_Avatar.png"
                alt="Profile"
                sx={{ width: 30, height: 30 }}
              />
              Elliot
            </Typography>
            <Box
              sx={{
                backgroundColor: "#f3f4f6",
                borderRadius: "12px",
                p: 2,
                mb: 2,
              }}
            >
              <Typography
                variant="body2"
                sx={{ lineHeight: 1.5, fontSize: "13px" }}
              >
                Hi I'm Elliot, your AI study assistant. I have access to all the
                reading comprehension passages, questions, answers, and
                explanations from your test. Feel free to ask me about any
                question, explanation, or concept you'd like to understand
                better!
              </Typography>

              <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
                <Typography variant="caption" sx={{ color: "#666" }}>
                  Helpful
                </Typography>
                <Divider orientation="vertical" flexItem />
                <Typography variant="caption" sx={{ color: "#666" }}>
                  Not-helpful
                </Typography>
              </Box>
            </Box>

            {/* Action Buttons */}
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 3 }}>
              <Box
                sx={{
                  border: "2px solid #f3f4f6",
                  borderRadius: "10px",
                  px: 2,
                  py: 1,
                  cursor: "pointer",
                  "&:hover": { bgcolor: "#E0EFFF" },
                }}
              >
                <Typography
                  variant="body2"
                  onClick={() => {
                    dispatch(
                      chatbotStart({
                        chat: "Explain current question",
                        question: `${questiondata}(Note: The image URL provided is for reference to be analyzed: ${imagedata})`,
                        session_id: "1",
                        passage: passage,
                        instruction: instruction,
                      })
                    );
                  }}
                >
                  Explain current question
                </Typography>
              </Box>
              <Box
                sx={{
                  border: "2px solid #f3f4f6",
                  borderRadius: "10px",
                  px: 2,
                  py: 1,
                  cursor: "pointer",
                  "&:hover": { bgcolor: "#E0EFFF" },
                }}
                onClick={() => {
                  dispatch(
                    chatbotStart({
                      chat: "Why is this answer correct?",
                      question: `${questiondata}(Note: The image URL provided is for reference to be analyzed: ${imagedata})`,
                      session_id: "1",
                      passage: passage,
                      instruction: instruction,
                    })
                  );
                }}
              >
                <Typography variant="body2">
                  Why is this answer correct?
                </Typography>
              </Box>
              <Box
                sx={{
                  border: "2px solid #f3f4f6",
                  borderRadius: "10px",
                  px: 2,
                  py: 1,
                  cursor: "pointer",
                  "&:hover": { bgcolor: "#E0EFFF" },
                }}
                onClick={() => {
                  dispatch(
                    chatbotStart({
                      chat: "Help with passage analysis",
                      question: `${questiondata}(Note: The image URL provided is for reference to be analyzed: ${imagedata})`,
                      session_id: "1",
                      passage: passage,
                      instruction: instruction,
                    })
                  );
                }}
              >
                <Typography variant="body2">
                  Help with passage analysis
                </Typography>
              </Box>
            </Box>

            {/* Study Tips Section */}
          </Box>
          {/* )} */}

          {/* Chat Messages */}
          {chatbotData?.data &&
            chatbotData.data?.map((data, index, array) => (
              <Box key={index} sx={{ mb: 2, px: 2 }}>
                {data.user === "user" ? (
                  <Markdown
                    children={data.message}
                    remarkPlugins={[remarkMath]}
                    rehypePlugins={[rehypeKatex]}
                    className="user_message"
                  />
                ) : (
                  <Box sx={{ display: "contents" }}>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 600,
                        mb: 1,
                        display: "flex",
                        gap: "10px",
                        alignItems: "center",
                      }}
                    >
                      <Avatar
                        sx={{ bgcolor: "#90caf9", width: 30, height: 30 }}
                      >
                        👦
                      </Avatar>
                      Elliot
                    </Typography>{" "}
                    <TypewriterMarkdown
                      content={data.message}
                      isLast={index === array.length - 1}
                      onFinish={() => setIsTyping(false)}
                      onUpdate={() => {
                        divRef.current?.lastElementChild?.scrollIntoView({
                          behavior: "smooth",
                          block: "start",
                          inline: "nearest",
                        });
                      }}
                      className="chat_bot_message"
                    />
                  </Box>
                )}
              </Box>
            ))}
        </div>
        <Divider />

        <footer
          className="chatbot_footer"
          style={{ padding: "0 16px 16px", marginTop: "10px" }}
        >
          <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(onSubmit)} ref={formRef}>
              <TextInputFieldC
                name="chat"
                type="text"
                className="chat_input"
                size="small"
                fullWidth
                inputRef={inputRef}
                disabled={false}
                placeholder="Ask Elliot about any question or concept..."

                InputProps={{
                  startAdornment: chatloading && (
                    <CircularProgress
                      size={24}
                      sx={{
                        color: "blue",
                        position: "absolute",
                        top: "50%",
                        left: "10px",
                        transform: "translateY(-50%)",
                      }}
                    />
                  ),
                  endAdornment: (
                    <IconButtonField
                      type="submit"
                      // disabled={chatloading || isTyping}
                      onClick={() => {
                        if (!chatloading && !isTyping) formRef.current?.requestSubmit();
                      }}
                      sx={{
                        pointerEvents: chatloading || isTyping ? "none" : "auto",
                      }}
                    >

                      <SvgIcon
                        component={Send}
                        sx={{
                          fontSize: "30px",
                          color: chatloading || isTyping ? "#bdbdbd" : "#1976d2"
                        }}
                        inheritViewBox
                      />
                    </IconButtonField>
                  ),
                  sx: {
                    borderRadius: "24px",
                    border: "2px solid #f3f4f6",
                    paddingLeft: chatloading || isTyping ? "40px" : "16px",
                  },
                }}
              />
            </form>
          </FormProvider>
          <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
            <Typography
              variant="caption"
              sx={{
                color: "#5481a8",
                ml: 1,
                display: "flex",
                justifyContent: "center",
                width: "100%",
                fontSize: "12px",
              }}
            >
              Elliot has access to all questions and explanations.
            </Typography>
          </Box>
        </footer>
      </Drawer>
    </>
  );
};

export default Chatbot;
